import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardContent } from "@/components/shared/ui/Card";
import { parseVideoUrl } from "@/lib/videoHelpers";
import { Lock } from "lucide-react";

interface IntroVideoWidgetProps {
  projectId: string;
}

export default function IntroVideoWidget({ projectId }: IntroVideoWidgetProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVideoAndAccess();
  }, [projectId]);

  const fetchVideoAndAccess = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Check access level
      const { data: accessData, error: accessError } = await supabase
        .from("user_project_access")
        .select("access_level")
        .eq("project_id", projectId)
        .eq("user_id", user.id)
        .single();

      if (accessError) throw accessError;

      // Only allow if access_level is 'visible'
      const canAccess = accessData?.access_level === "visible";
      setHasAccess(canAccess);

      // Fetch video URL from projects table
      const { data: projectData, error: fetchError } = await supabase
        .from("projects")
        .select("intro_video_url")
        .eq("id", projectId)
        .single();

      if (fetchError) throw fetchError;

      if (projectData?.intro_video_url) {
        setVideoUrl(projectData.intro_video_url);
      } else {
        setVideoUrl(null);
      }
    } catch (err) {
      console.error("Error fetching intro video:", err);
      setError("Failed to load video");
    } finally {
      setLoading(false);
    }
  };

  // Don't render if loading
  if (loading) {
    return (
      <Card variant="bordered">
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">Loading video...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't render if no video URL exists
  if (!videoUrl) {
    return null;
  }

  // Don't render if there's an error
  if (error) {
    return null;
  }

  // Show locked overlay if no access
  if (!hasAccess) {
    return (
      <Card variant="bordered">
        <CardContent>
          <div
            className="relative w-full bg-gray-100 flex items-center justify-center"
            style={{ paddingBottom: "56.25%" }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-transparent to-white/90">
              <Lock className="w-8 h-8 text-gray-400" />
              <p className="text-sm font-semibold text-gray-700">
                Video Access Restricted
              </p>
              <p className="text-xs text-gray-500">
                Contact us to request full access
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Parse the video URL
  const parsedVideo = parseVideoUrl(videoUrl);
  if (!parsedVideo) {
    console.error("Invalid video URL:", videoUrl);
    return null;
  }

  return (
    <Card variant="bordered">
      <CardContent>
        <div className="relative w-48 mx-auto h-96">
          {/* 192px wide, 384px tall - good phone-sized preview */}
          {parsedVideo.type === "youtube" && (
            <iframe
              src={parsedVideo.embedUrl}
              title="Introduction Video"
              className="absolute top-0 left-0 w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          {parsedVideo.type === "vimeo" && (
            <iframe
              src={parsedVideo.embedUrl}
              title="Introduction Video"
              className="absolute top-0 left-0 w-full h-full border-0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )}
          {parsedVideo.type === "descript" && (
            <iframe
              src={parsedVideo.embedUrl}
              title="Introduction Video"
              className="absolute top-0 left-0 w-full h-full border-0"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          )}
          {parsedVideo.type === "direct" && (
            <video
              src={parsedVideo.embedUrl}
              controls
              className="absolute top-0 left-0 w-full h-full"
              style={{ objectFit: "cover" }}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
